import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const TopInvestmentsChart = ({ selectedManager, className = "" }) => {
  const [draftData, setDraftData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load draft data independently (same as DraftExplorer)
  useEffect(() => {
    const loadDraftData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if we have the uploaded file available
        if (typeof window !== 'undefined' && window.fs && window.fs.readFile) {
          const arrayBuffer = await window.fs.readFile('BoBData.xlsx');
          
          if (!window.XLSX) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            script.onload = () => {
              parseExcelData(arrayBuffer);
            };
            script.onerror = () => {
              setError('Failed to load XLSX library');
              setLoading(false);
            };
            document.head.appendChild(script);
          } else {
            parseExcelData(arrayBuffer);
          }
        } else {
          // Fallback: try to fetch from public folder
          const response = await fetch('/BoBData.xlsx');
          if (!response.ok) {
            throw new Error('Could not fetch Excel file');
          }
          const arrayBuffer = await response.arrayBuffer();
          
          if (!window.XLSX) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            script.onload = () => {
              parseExcelData(arrayBuffer);
            };
            script.onerror = () => {
              setError('Failed to load XLSX library');
              setLoading(false);
            };
            document.head.appendChild(script);
          } else {
            parseExcelData(arrayBuffer);
          }
        }
      } catch (error) {
        console.error('Error loading draft data:', error);
        setError('Error loading draft data: ' + error.message);
        setLoading(false);
      }
    };

    const parseExcelData = (arrayBuffer) => {
      try {
        const workbook = window.XLSX.read(arrayBuffer, { type: 'array' });
        
        // Use the same logic as DraftExplorer to find the draft data
        let draftSheet = null;
        let sheetName = '';
        
        // Try to find the correct sheet - could be at index 24 or named something specific
        if (workbook.SheetNames.length >= 25) {
          sheetName = workbook.SheetNames[24]; // 0-indexed, so sheet 25 is index 24
          draftSheet = workbook.Sheets[sheetName];
        }
        
        // If that doesn't work, look for a sheet with draft-related data
        if (!draftSheet) {
          const draftSheetNames = workbook.SheetNames.filter(name => 
            name.toLowerCase().includes('draft') || 
            name.toLowerCase().includes('dd') ||
            name.toLowerCase().includes('player') ||
            name.toLowerCase().includes('keeper')
          );
          
          if (draftSheetNames.length > 0) {
            sheetName = draftSheetNames[0];
            draftSheet = workbook.Sheets[sheetName];
          }
        }
        
        if (!draftSheet) {
          throw new Error('Draft data sheet not found');
        }
        
        const jsonData = window.XLSX.utils.sheet_to_json(draftSheet);
        
        // Process and validate the data structure
        const processedData = jsonData.map((row) => {
          const year = row.Year || row.year || row.YEAR;
          const owner = row.Owner || row.owner || row.OWNER || row.Manager || row.manager;
          const player = row.Player || row.player || row.PLAYER || row.Name || row.name;
          const position = row.Position || row.position || row.POSITION || row.Pos || row.pos;
          const cost = parseFloat(row.Cost || row.cost || row.COST || row.Price || row.price || 0);
          
          // Validate required fields
          if (!year || !owner || !player || !position || isNaN(cost)) {
            return null;
          }
          
          return {
            Year: parseInt(year),
            Manager: String(owner).trim(),
            Player: String(player).trim(),
            Position: String(position).trim().toUpperCase(),
            Cost: cost
          };
        }).filter(Boolean);
        
        setDraftData(processedData);
        setLoading(false);
        
      } catch (error) {
        console.error('Error parsing Excel data:', error);
        setError('Error parsing Excel data: ' + error.message);
        setLoading(false);
      }
    };

    loadDraftData();
  }, []);

  // Calculate manager's top investments with detailed year breakdown for stacked bars
  const calculateTopInvestments = () => {
    if (!draftData.length || !selectedManager) return [];

    // Filter data for the selected manager
    const managerData = draftData.filter(row => row.Manager === selectedManager);
    
    if (!managerData.length) return [];

    // Get all unique years to create consistent data structure
    const allYears = [...new Set(managerData.map(row => row.Year))].sort((a, b) => a - b);

    // Group by player and track year-by-year investments
    const playerInvestments = managerData.reduce((acc, row) => {
      const player = row.Player;
      const year = row.Year;
      const cost = row.Cost || 0;
      
      if (!acc[player]) {
        acc[player] = { total: 0, years: {} };
        // Initialize all years to 0
        allYears.forEach(y => acc[player].years[y] = 0);
      }
      
      acc[player].total += cost;
      acc[player].years[year] += cost;
      
      return acc;
    }, {});

    // Convert to array with year breakdown for stacking
    return Object.entries(playerInvestments)
      .map(([player, data]) => {
        const isMobile = window.innerWidth < 640;
        const truncateLength = isMobile ? 8 : 15;
        
        const result = {
          player: player.length > truncateLength ? player.substring(0, truncateLength) + '...' : player,
          fullPlayerName: player,
          investment: data.total,
          yearBreakdown: Object.entries(data.years)
            .filter(([year, cost]) => cost > 0)
            .map(([year, cost]) => ({ year: parseInt(year), cost }))
            .sort((a, b) => a.year - b.year)
        };
        
        // Add individual year data for stacking
        allYears.forEach(year => {
          result[`year_${year}`] = data.years[year] || 0;
        });
        
        return result;
      })
      .sort((a, b) => b.investment - a.investment)
      .slice(0, window.innerWidth < 640 ? 10 : 20) // Show fewer on mobile
      .map((item, index) => ({ ...item, rank: index + 1 }));
  };

  const topInvestments = calculateTopInvestments();
  
  // Get all years for creating stacked bars and color mapping
  const allYears = [...new Set(draftData.filter(row => row.Manager === selectedManager).map(row => row.Year))].sort((a, b) => a - b);
  
  // Color palette for different years
  const yearColors = {
    2012: '#8b5cf6', // Purple
    2013: '#6366f1', // Indigo  
    2014: '#3b82f6', // Blue
    2015: '#06b6d4', // Cyan
    2016: '#10b981', // Emerald
    2017: '#84cc16', // Lime
    2018: '#eab308', // Yellow
    2019: '#f59e0b', // Amber
    2020: '#ef4444', // Red
    2021: '#ec4899', // Pink
    2022: '#a855f7', // Violet
    2023: '#14b8a6', // Teal
    2024: '#f97316'  // Orange
  };

  // Custom tooltip component with mobile optimization
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isMobile = window.innerWidth < 640;
      
      return (
        <div
          style={{
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            border: 'none',
            borderRadius: isMobile ? '8px' : '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            color: 'white',
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: '500',
            backdropFilter: 'blur(8px)',
            padding: isMobile ? '8px 12px' : '12px 16px',
            maxWidth: isMobile ? '200px' : '250px'
          }}
        >
          {/* Player name and rank */}
          <div style={{ 
            fontSize: isMobile ? '14px' : '16px', 
            fontWeight: '600', 
            marginBottom: isMobile ? '6px' : '8px', 
            color: '#ffffff' 
          }}>
            #{data.rank} {data.fullPlayerName}
          </div>
          
          {/* Total investment */}
          <div style={{ 
            fontSize: isMobile ? '12px' : '14px', 
            color: '#93c5fd', 
            marginBottom: isMobile ? '6px' : '8px', 
            fontWeight: '600' 
          }}>
            Total: ${data.investment}
          </div>
          
          {/* Year breakdown with color indicators */}
          <div style={{ fontSize: isMobile ? '10px' : '12px', color: '#d1d5db' }}>
            <div style={{ 
              fontWeight: '600', 
              marginBottom: isMobile ? '4px' : '6px', 
              color: '#e5e7eb' 
            }}>
              {isMobile ? 'Years:' : 'Year Breakdown:'}
            </div>
            {data.yearBreakdown?.slice(0, isMobile ? 3 : 10).map((yearData, index) => (
              <div key={index} style={{ 
                marginBottom: isMobile ? '2px' : '4px', 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between' 
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px' }}>
                  <div
                    style={{
                      width: isMobile ? '8px' : '12px',
                      height: isMobile ? '8px' : '12px',
                      borderRadius: '2px',
                      backgroundColor: yearColors[yearData.year],
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }}
                  />
                  <span>{yearData.year}:</span>
                </div>
                <span style={{ fontWeight: '500' }}>${yearData.cost}</span>
              </div>
            ))}
            {isMobile && data.yearBreakdown?.length > 3 && (
              <div style={{ 
                fontSize: '9px', 
                color: '#9ca3af', 
                textAlign: 'center', 
                marginTop: '4px' 
              }}>
                +{data.yearBreakdown.length - 3} more years
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">
            <span className="hidden sm:inline">
              {selectedManager}'s Top Investments by Total Draft Budget
            </span>
            <span className="sm:hidden">
              {selectedManager}'s Top Investments
            </span>
          </h3>
          <p className="text-gray-600 text-sm">
            <span className="hidden sm:inline">Top 20 players by total investment amount</span>
            <span className="sm:hidden">Top investments by total cost</span>
          </p>
        </div>
        
        <div className="flex items-center justify-center h-64 sm:h-80 lg:h-96">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 border-4 border-gray-200 border-t-purple-600 rounded-full animate-spin"></div>
            <span className="text-gray-600 font-medium text-sm sm:text-base">Loading investment data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="text-center text-red-500 py-12">
          <div className="text-sm sm:text-base">Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!topInvestments.length) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <div className="text-center text-gray-500 py-12">
          <div className="text-sm sm:text-base">No investment data found for {selectedManager}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
      {/* Title with mobile optimization */}
      <div className="mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">
          <span className="hidden sm:inline">
            {selectedManager}'s Top Investments by Total Draft Budget
          </span>
          <span className="sm:hidden">
            {selectedManager}'s Top Investments
          </span>
        </h3>
        <p className="text-gray-600 text-sm">
          <span className="hidden sm:inline">
            Top {window.innerWidth < 640 ? 10 : 20} players by total investment amount
          </span>
          <span className="sm:hidden">
            Top investments by total cost
          </span>
        </p>
      </div>

      {/* Enhanced Stacked Chart with responsive height */}
      <div className="h-64 sm:h-80 lg:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={topInvestments}
            margin={{ 
              top: 10, 
              right: window.innerWidth < 640 ? 10 : 30, 
              left: window.innerWidth < 640 ? 10 : 20, 
              bottom: window.innerWidth < 640 ? 60 : 80 
            }}
          >
            <defs>
              {/* Create gradients for each year */}
              {allYears.map(year => (
                <linearGradient key={year} id={`gradient_${year}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={yearColors[year]} />
                  <stop offset="100%" stopColor={yearColors[year]} stopOpacity={0.8} />
                </linearGradient>
              ))}
              <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#00000015"/>
              </filter>
            </defs>
            
            <XAxis 
              dataKey="player" 
              fontSize={window.innerWidth < 640 ? 9 : 11}
              angle={-45}
              textAnchor="end"
              height={window.innerWidth < 640 ? 60 : 80}
              tick={{ fill: '#6b7280', fontWeight: 500 }}
              axisLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
              tickLine={{ stroke: '#e5e7eb' }}
              interval={0}
            />
            
            <YAxis 
              fontSize={window.innerWidth < 640 ? 10 : 12}
              width={window.innerWidth < 640 ? 30 : 40}
              tickFormatter={(value) => window.innerWidth < 640 ? `${value}` : `${value}`}
              tick={{ fill: '#6b7280', fontWeight: 500 }}
              axisLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
              tickLine={{ stroke: '#e5e7eb' }}
              domain={[0, 'dataMax + 10']}
            />
            
            <CartesianGrid 
              strokeDasharray="2 4" 
              stroke="#f3f4f6" 
              strokeOpacity={0.8}
              vertical={false}
            />
            
            <Tooltip
              content={CustomTooltip}
              cursor={{ fill: 'rgba(139, 92, 246, 0.05)' }}
            />
            
            {/* Create stacked bars for each year with responsive sizing */}
            {allYears.map((year, index) => (
              <Bar 
                key={year}
                dataKey={`year_${year}`}
                stackId="investment"
                fill={`url(#gradient_${year})`}
                radius={index === allYears.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                filter="url(#shadow)"
                maxBarSize={window.innerWidth < 640 ? 25 : 35}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Mobile-specific year legend */}
      <div className="mt-3 sm:hidden">
        <div className="text-xs font-medium text-gray-700 mb-2">Years:</div>
        <div className="grid grid-cols-4 gap-1 text-xs">
          {allYears.slice(0, 8).map(year => (
            <div key={year} className="flex items-center space-x-1">
              <div 
                className="w-2 h-2 rounded-sm flex-shrink-0"
                style={{ backgroundColor: yearColors[year] }}
              />
              <span className="text-gray-600 truncate">{year}</span>
            </div>
          ))}
        </div>
        {allYears.length > 8 && (
          <div className="text-center mt-2">
            <span className="text-xs text-gray-500">+{allYears.length - 8} more years</span>
          </div>
        )}
      </div>

      {/* Desktop year legend */}
      <div className="hidden sm:block mt-4">
        <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
          {allYears.map(year => (
            <div key={year} className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: yearColors[year] }}
              />
              <span className="text-gray-700">{year}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopInvestmentsChart;